export const MAX_DISCOUNT_INPUT_LENGTH = 128
export const MAX_DISCOUNT_STEPS = 3
export const DISCOUNT_MONEY_DECIMAL_PLACES = 2
export const DISCOUNT_PERCENT_DECIMAL_PLACES = 4

export type DiscountMode = 'direct' | 'reverse' | 'compare'
export type DiscountAnalyticsMode = 'direct' | 'stacked' | 'reverse' | 'compare'
export type DiscountField = 'originalPrice' | 'finalPrice' | 'discount-0' | 'discount-1' | 'discount-2'
export type DiscountCalculationError =
  | 'empty_input'
  | 'invalid_number'
  | 'number_too_long'
  | 'negative_price'
  | 'discount_out_of_range'
  | 'zero_original_price'
  | 'new_price_above_old'
  | 'irreversible_discount'

interface Rational {
  numerator: bigint
  denominator: bigint
}

export interface DiscountInputs {
  originalPrice: string
  finalPrice: string
  discounts: string[]
}

export interface FormattedDiscountValue {
  text: string
  rounded: boolean
}

export interface DiscountCalculationResult {
  originalPrice: FormattedDiscountValue
  finalPrice: FormattedDiscountValue
  savings: FormattedDiscountValue
  effectiveDiscount: FormattedDiscountValue
  formula: string
  substitution: string
  analyticsMode: DiscountAnalyticsMode
}

export type DiscountCalculation =
  | { status: 'success'; result: DiscountCalculationResult }
  | { status: 'error'; error: DiscountCalculationError; invalidFields: DiscountField[] }

const DECIMAL_PATTERN = /^[+-]?(?:\d+|\d+[.,]\d+|[.,]\d+)$/
const ZERO = rational(0n)
const HUNDRED = rational(100n)

function absolute(value: bigint): bigint {
  return value < 0n ? -value : value
}

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = absolute(left)
  let b = absolute(right)
  while (b !== 0n) {
    const remainder = a % b
    a = b
    b = remainder
  }
  return a || 1n
}

function rational(numerator: bigint, denominator = 1n): Rational {
  if (denominator === 0n) throw new RangeError('Rational denominator cannot be zero')
  const sign = denominator < 0n ? -1n : 1n
  const divisor = greatestCommonDivisor(numerator, denominator)
  return {
    numerator: (numerator / divisor) * sign,
    denominator: absolute(denominator / divisor),
  }
}

function subtract(left: Rational, right: Rational): Rational {
  return rational(
    left.numerator * right.denominator - right.numerator * left.denominator,
    left.denominator * right.denominator,
  )
}

function multiply(left: Rational, right: Rational): Rational {
  return rational(left.numerator * right.numerator, left.denominator * right.denominator)
}

function divide(left: Rational, right: Rational): Rational {
  return rational(left.numerator * right.denominator, left.denominator * right.numerator)
}

function compare(left: Rational, right: Rational): number {
  const delta = left.numerator * right.denominator - right.numerator * left.denominator
  return delta < 0n ? -1 : delta > 0n ? 1 : 0
}

function parseDecimal(input: string): Rational | null {
  const normalized = input.trim().replace(',', '.')
  if (!DECIMAL_PATTERN.test(normalized)) return null
  const negative = normalized.startsWith('-')
  const unsigned = normalized.replace(/^[+-]/, '')
  const [integer = '0', fraction = ''] = unsigned.split('.')
  const digits = `${integer || '0'}${fraction}`.replace(/^0+(?=\d)/, '') || '0'
  return rational(BigInt(`${negative ? '-' : ''}${digits}`), 10n ** BigInt(fraction.length))
}

function parseField(
  value: string,
  field: DiscountField,
): { value: Rational } | { error: DiscountCalculationError; field: DiscountField } {
  if (value.trim() === '') return { error: 'empty_input', field }
  if (value.length > MAX_DISCOUNT_INPUT_LENGTH) return { error: 'number_too_long', field }
  const parsed = parseDecimal(value)
  if (!parsed) return { error: 'invalid_number', field }
  return { value: parsed }
}

function format(value: Rational, decimalPlaces: number): FormattedDiscountValue {
  const factor = 10n ** BigInt(decimalPlaces)
  const scaledNumerator = absolute(value.numerator) * factor
  let roundedInteger = scaledNumerator / value.denominator
  const remainder = scaledNumerator % value.denominator
  if (remainder * 2n >= value.denominator) roundedInteger += 1n
  const integer = roundedInteger / factor
  const fraction = (roundedInteger % factor)
    .toString()
    .padStart(decimalPlaces, '0')
    .replace(/0+$/, '')
  const magnitude = fraction ? `${integer}.${fraction}` : integer.toString()
  return {
    text: `${value.numerator < 0n && roundedInteger !== 0n ? '-' : ''}${magnitude}`,
    rounded: remainder !== 0n,
  }
}

function formatExactTerminating(value: Rational): string {
  let denominator = value.denominator
  let powersOfTwo = 0
  let powersOfFive = 0
  while (denominator % 2n === 0n) {
    denominator /= 2n
    powersOfTwo += 1
  }
  while (denominator % 5n === 0n) {
    denominator /= 5n
    powersOfFive += 1
  }
  if (denominator !== 1n) return `${value.numerator}/${value.denominator}`

  const decimalPlaces = Math.max(powersOfTwo, powersOfFive)
  const scaled = absolute(value.numerator)
    * (2n ** BigInt(decimalPlaces - powersOfTwo))
    * (5n ** BigInt(decimalPlaces - powersOfFive))
  if (decimalPlaces === 0) return value.numerator.toString()

  const digits = scaled.toString().padStart(decimalPlaces + 1, '0')
  const integer = digits.slice(0, -decimalPlaces)
  const fraction = digits.slice(-decimalPlaces).replace(/0+$/, '')
  const magnitude = fraction ? `${integer}.${fraction}` : integer
  return `${value.numerator < 0n && scaled !== 0n ? '-' : ''}${magnitude}`
}

function parseDiscounts(
  inputs: DiscountInputs,
): { values: Rational[] } | { error: DiscountCalculationError; fields: DiscountField[] } {
  if (inputs.discounts.length < 1 || inputs.discounts.length > MAX_DISCOUNT_STEPS) {
    return { error: 'discount_out_of_range', fields: ['discount-0'] }
  }

  const values: Rational[] = []
  for (const [index, input] of inputs.discounts.entries()) {
    const field = `discount-${index}` as DiscountField
    const parsed = parseField(input, field)
    if ('error' in parsed) return { error: parsed.error, fields: [parsed.field] }
    if (compare(parsed.value, ZERO) < 0 || compare(parsed.value, HUNDRED) > 0) {
      return { error: 'discount_out_of_range', fields: [field] }
    }
    values.push(parsed.value)
  }
  return { values }
}

function discountFactor(discounts: Rational[]): Rational {
  return discounts.reduce(
    (factor, discount) => multiply(factor, divide(subtract(HUNDRED, discount), HUNDRED)),
    rational(1n),
  )
}

function result(
  originalPrice: Rational,
  finalPrice: Rational,
  formula: string,
  substitution: string,
  analyticsMode: DiscountAnalyticsMode,
  knownEffectiveDiscount?: Rational,
): DiscountCalculation {
  const savings = subtract(originalPrice, finalPrice)
  const effectiveDiscount = knownEffectiveDiscount ?? (compare(originalPrice, ZERO) === 0
    ? ZERO
    : divide(multiply(savings, HUNDRED), originalPrice))
  return {
    status: 'success',
    result: {
      originalPrice: format(originalPrice, DISCOUNT_MONEY_DECIMAL_PLACES),
      finalPrice: format(finalPrice, DISCOUNT_MONEY_DECIMAL_PLACES),
      savings: format(savings, DISCOUNT_MONEY_DECIMAL_PLACES),
      effectiveDiscount: format(effectiveDiscount, DISCOUNT_PERCENT_DECIMAL_PLACES),
      formula,
      substitution,
      analyticsMode,
    },
  }
}

export function calculateDiscount(
  mode: DiscountMode,
  inputs: DiscountInputs,
): DiscountCalculation {
  if (mode === 'compare') {
    const original = parseField(inputs.originalPrice, 'originalPrice')
    if ('error' in original) return { status: 'error', error: original.error, invalidFields: [original.field] }
    const final = parseField(inputs.finalPrice, 'finalPrice')
    if ('error' in final) return { status: 'error', error: final.error, invalidFields: [final.field] }
    if (compare(original.value, ZERO) < 0) {
      return { status: 'error', error: 'negative_price', invalidFields: ['originalPrice'] }
    }
    if (compare(final.value, ZERO) < 0) {
      return { status: 'error', error: 'negative_price', invalidFields: ['finalPrice'] }
    }
    if (compare(original.value, ZERO) === 0) {
      return { status: 'error', error: 'zero_original_price', invalidFields: ['originalPrice'] }
    }
    if (compare(final.value, original.value) > 0) {
      return { status: 'error', error: 'new_price_above_old', invalidFields: ['finalPrice'] }
    }
    return result(
      original.value,
      final.value,
      'Скидка = (старая цена − новая цена) ÷ старая цена × 100%',
      `(${formatExactTerminating(original.value)} − ${formatExactTerminating(final.value)}) ÷ ${formatExactTerminating(original.value)} × 100%`,
      'compare',
    )
  }

  const discounts = parseDiscounts(inputs)
  if ('error' in discounts) {
    return { status: 'error', error: discounts.error, invalidFields: discounts.fields }
  }
  const factor = discountFactor(discounts.values)
  const effectiveDiscount = subtract(HUNDRED, multiply(factor, HUNDRED))
  const percentages = discounts.values.map((value) => `${formatExactTerminating(value)}%`)
  const factors = discounts.values.map((value) => `(1 − ${formatExactTerminating(value)} ÷ 100)`)

  if (mode === 'direct') {
    const original = parseField(inputs.originalPrice, 'originalPrice')
    if ('error' in original) return { status: 'error', error: original.error, invalidFields: [original.field] }
    if (compare(original.value, ZERO) < 0) {
      return { status: 'error', error: 'negative_price', invalidFields: ['originalPrice'] }
    }
    const finalPrice = multiply(original.value, factor)
    return result(
      original.value,
      finalPrice,
      `Итоговая цена = исходная цена × ${factors.join(' × ')}`,
      `${formatExactTerminating(original.value)} × ${factors.join(' × ')}; скидки: ${percentages.join(', ')}`,
      discounts.values.length === 1 ? 'direct' : 'stacked',
      effectiveDiscount,
    )
  }

  if (compare(factor, ZERO) === 0) {
    return {
      status: 'error',
      error: 'irreversible_discount',
      invalidFields: discounts.values
        .map((value, index) => compare(value, HUNDRED) === 0 ? `discount-${index}` as DiscountField : null)
        .filter((field): field is DiscountField => field !== null),
    }
  }
  const final = parseField(inputs.finalPrice, 'finalPrice')
  if ('error' in final) return { status: 'error', error: final.error, invalidFields: [final.field] }
  if (compare(final.value, ZERO) < 0) {
    return { status: 'error', error: 'negative_price', invalidFields: ['finalPrice'] }
  }
  const originalPrice = divide(final.value, factor)
  return result(
    originalPrice,
    final.value,
    `Исходная цена = итоговая цена ÷ (${factors.join(' × ')})`,
    `${formatExactTerminating(final.value)} ÷ (${factors.join(' × ')}); скидки: ${percentages.join(', ')}`,
    'reverse',
    effectiveDiscount,
  )
}
